import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

/**
 * Button — CHANGED in 1.17.0-dev.1 to the Vikar Experten CTA, with the client's
 * action semantics on top of it.
 *
 * COLOUR MEANS SOMETHING HERE:
 *   variant="primary" | "cta"  blue   — do it: log in, save, send, submit
 *   variant="accent"           green  — approve, confirm, release
 *   variant="danger"           red    — reject, dispute, delete (outline, fills
 *                                       on hover so it never shouts at rest)
 *   variant="outline"|"ghost"  blue   — quiet actions
 * The website uses red for every CTA; that cannot survive in a tool where a
 * customer approves or rejects a week's work. Brand red keeps the masthead, the
 * logo ground and the splitter.
 *
 * What moved from the old blue/orange button:
 *   - 6px corners (the client's approved value; the website is square at 0).
 *   - Uppercase, bold, 1.4px tracking — the brand's CTA label treatment, and
 *     the one place in the whole system where letter-spacing is used.
 *   - Hover darkens the button's own fill and adds the pressed inset. (Green is
 *     no longer the universal hover — it carries meaning now.)
 *   - Focus is a 3px halo in the button's own action colour.
 *   - 100ms ease-in-out, matching every other control.
 *
 * What was added:
 *   - variant="cta" — the hero action. Blue plate, plus the arrow that fades in
 *     and slides from 75% to 88% over 500ms on an overshoot curve. That bounce
 *     is the brand's only piece of personality; it belongs on the one primary
 *     action of a page (log in, send for approval), not on toolbar buttons.
 *   - variant="danger".
 *
 * Existing call sites keep working: primary | secondary | accent | outline |
 * ghost all still resolve, and `size`, `fullWidth`, `to`, `icon`,
 * `iconPosition` are unchanged.
 */
const BaseButton = styled.button`
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:${({ theme }) => theme.spacing[2]};
  position:relative;
  overflow:hidden;
  padding:${({ size, theme }) =>
    size === 'sm' ? `${theme.spacing[2]} ${theme.spacing[3]}` :
    size === 'lg' ? `${theme.spacing[4]} ${theme.spacing[6]}` :
    `${theme.spacing[3]} ${theme.spacing[5]}`};
  font-size:${({ size, theme }) =>
    size === 'sm' ? theme.typography.fontSize.xs :
    size === 'lg' ? theme.typography.fontSize.md :
    theme.typography.fontSize.base};
  font-weight:${({ theme }) => theme.typography.fontWeight.bold};
  text-transform:uppercase;
  letter-spacing:${({ theme }) => theme.typography.letterSpacing.cta};
  line-height:1.2;
  border-radius:${({ theme }) => theme.borders.radius.content};
  transition:${({ theme }) => theme.transitions.default};
  cursor:pointer;
  width:${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  min-height:${({ size }) => (size === 'lg' ? '50px' : '44px')};

  background-color:${({ theme, variant }) =>
    variant === 'secondary' ? theme.components.button.secondary.backgroundColor :
    variant === 'accent' ? theme.components.button.accent.backgroundColor :
    variant === 'danger' || variant === 'outline' || variant === 'ghost' ? 'transparent' :
    theme.components.button.primary.backgroundColor};
  color:${({ theme, variant }) =>
    variant === 'accent' ? theme.components.button.accent.color :
    variant === 'danger' ? theme.components.button.danger.color :
    variant === 'outline' ? theme.components.button.outline.color :
    variant === 'ghost' ? theme.components.button.ghost.color :
    theme.colors.white};
  border:${({ theme, variant }) =>
    variant === 'danger' ? `1px solid ${theme.components.button.danger.borderColor}` :
    variant === 'outline' ? `1px solid ${theme.components.button.outline.borderColor}` : 'none'};

  &:hover{
    background-color:${({ theme, variant }) =>
      variant === 'accent' ? theme.components.button.accent.hoverBackgroundColor :
      variant === 'danger' ? theme.components.button.danger.hoverBackgroundColor :
      variant === 'outline' ? theme.components.button.outline.hoverBackgroundColor :
      variant === 'ghost' ? theme.components.button.ghost.hoverBackgroundColor :
      variant === 'secondary' ? theme.components.button.secondary.hoverBackgroundColor :
      theme.components.button.primary.hoverBackgroundColor};
    border-color:${({ theme, variant }) =>
      variant === 'danger' ? theme.components.button.danger.hoverBackgroundColor :
      variant === 'outline' ? theme.components.button.outline.hoverBackgroundColor : 'transparent'};
    color:${({ theme, variant }) =>
      variant === 'accent' ? theme.components.button.accent.hoverColor :
      variant === 'ghost' ? theme.components.button.ghost.hoverColor :
      theme.colors.white};
    box-shadow:${({ theme, variant }) => (variant === 'ghost' ? 'none' : theme.shadows.press)};
  }

  &:focus-visible{
    outline:none;
    box-shadow:${({ theme, variant }) =>
      variant === 'accent' ? theme.shadows.focusAccent :
      variant === 'danger' ? theme.shadows.focusDanger :
      theme.shadows.focus};
  }

  &:disabled{opacity:.6;cursor:not-allowed;box-shadow:none}
  &:disabled:hover{
    background-color:${({ theme, variant }) =>
      variant === 'danger' || variant === 'outline' || variant === 'ghost' ? 'transparent' :
      variant === 'accent' ? theme.components.button.accent.backgroundColor :
      theme.components.button.primary.backgroundColor};
    color:${({ theme, variant }) =>
      variant === 'danger' ? theme.components.button.danger.color :
      variant === 'outline' || variant === 'ghost' ? theme.components.button.outline.color :
      variant === 'accent' ? theme.components.button.accent.color :
      theme.colors.white};
    border-color:${({ theme, variant }) =>
      variant === 'danger' ? theme.components.button.danger.borderColor :
      variant === 'outline' ? theme.components.button.outline.borderColor : 'transparent'};
  }

  .button-icon{display:inline-flex;align-items:center}
  svg{width:1em;height:1em}

  /* --- the CTA arrow: 500ms, 200ms delay, overshoot curve ----------------- */
  ${({ variant, theme }) => variant === 'cta' && `
    .button-text{transition:${theme.transitions.cta};display:block}
    &::after{
      content:"\\f0a9";
      font-family:"Font Awesome 5 Free";
      font-weight:900;
      position:absolute;top:50%;left:75%;
      transform:translateY(-50%);
      opacity:0;
      transition:${theme.transitions.cta};
    }
    &:hover .button-text{transform:translateX(-12px)}
    &:hover::after{left:88%;opacity:1}
  `}
`;

const LinkButton = styled(BaseButton).attrs({ as: Link })`
  text-decoration:none;
`;

const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  to,
  icon,
  iconPosition = 'left',
  ...props
}) => {
  const iconOnly = !children && icon;

  const renderContent = () => (
    <>
      {icon && iconPosition === 'left' && <span className="button-icon">{icon}</span>}
      {children && <span className="button-text">{children}</span>}
      {icon && iconPosition === 'right' && <span className="button-icon">{icon}</span>}
    </>
  );

  if (to) {
    return (
      <LinkButton to={to} variant={variant} size={size} fullWidth={fullWidth} iconOnly={iconOnly} {...props}>
        {renderContent()}
      </LinkButton>
    );
  }

  return (
    <BaseButton variant={variant} size={size} fullWidth={fullWidth} iconOnly={iconOnly} {...props}>
      {renderContent()}
    </BaseButton>
  );
};

export default Button;
